import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    visitDate: '',
    visitTime: '',
    relationship: '',
    elderAge: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // 存入 Supabase 資料庫
      const { error: dbError } = await supabase
        .from('visit_bookings')
        .insert({
          name: formData.name,
          phone: formData.phone,
          email: formData.email || null,
          visit_date: formData.visitDate,
          visit_time: formData.visitTime,
          relationship: formData.relationship,
          elder_age: formData.elderAge || null,
          message: formData.message || null,
          status: '待確認',
        });

      if (dbError) {
        setSubmitStatus('error');
        return;
      }

      // 同時提交到 Readdy form
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('visitDate', formData.visitDate);
      formBody.append('visitTime', formData.visitTime);
      formBody.append('relationship', formData.relationship);
      formBody.append('elderAge', formData.elderAge);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d62m6taa3ljmq1ecua8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        visitDate: '',
        visitTime: '',
        relationship: '',
        elderAge: '',
        message: ''
      });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="visit" className="py-24 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-base font-medium mb-4">
            歡迎蒞臨參觀
          </span>
          <h2 className="text-4xl font-bold text-gray-800 mb-4">預約參觀</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            歡迎您預約參觀前金日照中心，親自感受我們溫馨的照護環境
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* 左側資訊 */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">參觀須知</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-1">開放參觀時間</h4>
                  <p className="text-gray-600">週一至週五 09:00 - 17:00</p>
                  <p className="text-gray-500 text-sm">建議提前3天預約</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-1">中心地址</h4>
                  <p className="text-gray-600">高雄市前金區文武二街163號5樓</p>
                  <p className="text-gray-500 text-sm">捷運前金站3號出口步行3分鐘</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-rose-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-1">聯絡電話</h4>
                  <p className="text-gray-600">07-2416568</p>
                  <p className="text-gray-500 text-sm">服務時間內皆可來電</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-purple-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-1">參觀流程</h4>
                  <p className="text-gray-600">環境導覽 → 服務說明 → 問答諮詢</p>
                  <p className="text-gray-500 text-sm"></p>
                </div>
              </div>
            </div>
          </div>

          {/* 右側表單 */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">填寫預約資料</h3>
            
            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-teal-600 text-4xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-800 mb-3">預約成功！</h4>
                <p className="text-gray-600 mb-6">我們將於1-2個工作天內與您聯繫確認參觀時間</p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再次預約
                </button>
              </div>
            ) : (
              <form 
                id="visit-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="請輸入您的姓名"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      聯絡電話 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="請輸入聯絡電話"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      希望參觀日期 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="visitDate"
                      value={formData.visitDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      希望參觀時段 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="visitTime"
                      value={formData.visitTime}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">請選擇時段</option>
                      <option value="09:00-10:00">09:00 - 10:00</option>
                      <option value="10:00-11:00">10:00 - 11:00</option>
                      <option value="11:00-12:00">11:00 - 12:00</option>
                      <option value="14:00-15:00">14:00 - 15:00</option>
                      <option value="15:00-16:00">15:00 - 16:00</option>
                      <option value="16:00-17:00">16:00 - 17:00</option>
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      與長輩關係 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">請選擇關係</option>
                      <option value="子女">子女</option>
                      <option value="配偶">配偶</option>
                      <option value="孫子女">孫子女</option>
                      <option value="其他親屬">其他親屬</option>
                      <option value="本人">本人</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      長輩年齡
                    </label>
                    <input
                      type="text"
                      name="elderAge"
                      value={formData.elderAge}
                      onChange={handleChange}
                      placeholder="請輸入長輩年齡（選填）"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    備註說明
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    placeholder="如有特殊需求或問題，請在此說明（選填）"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-600 text-base">提交失敗，請稍後再試或直接來電預約</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-teal-600 text-white rounded-xl text-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line"></i>
                      送出預約
                    </>
                  )}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  提交後我們將於1-2個工作天內與您聯繫確認
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
